import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FilteredResultsList = ({ results, onViewDetails }) => {
  if (results?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-xl p-6 md:p-8 lg:p-12 text-center">
        <div className="flex flex-col items-center gap-4 max-w-md mx-auto">
          <div className="flex items-center justify-center w-16 h-16 md:w-20 md:h-20 bg-muted rounded-full">
            <Icon name="SearchX" size={32} className="text-muted-foreground" />
          </div>
          <div>
            <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground mb-2">
              No Venues Found
            </h3>
            <p className="text-sm md:text-base text-muted-foreground">
              Try adjusting your filters or expanding the search radius to see more options.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="p-4 md:p-6 border-b border-border bg-muted/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 bg-success/10 rounded-lg">
              <Icon name="MapPin" size={20} className="text-success" />
            </div>
            <div>
              <h3 className="font-heading text-base md:text-lg font-semibold text-foreground">
                Filtered Results
              </h3>
              <p className="caption text-muted-foreground">
                <span className="font-semibold text-success data-text">{results?.length}</span> venues match your criteria
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="max-h-[600px] overflow-y-auto scrollbar-custom">
        <div className="divide-y divide-border">
          {results?.map((venue) => (
            <div
              key={venue?.id}
              className="p-4 md:p-6 hover:bg-muted/50 transition-smooth"
            >
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-3 mb-3">
                    <div className={`
                      flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-lg flex-shrink-0
                      ${venue?.typeColor}
                    `}>
                      <Icon name={venue?.typeIcon} size={20} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-heading text-base md:text-lg font-semibold text-foreground mb-1 truncate">
                        {venue?.name}
                      </h4>
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium">
                          <Icon name={venue?.typeIcon} size={12} />
                          {venue?.type}
                        </span>
                        {venue?.rating && (
                          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                            <Icon name="Star" size={12} className="text-amber-500 fill-amber-500" />
                            <span className="font-semibold data-text">{venue?.rating}</span>
                            <span>({venue?.reviewCount})</span>
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground flex items-start gap-2">
                        <Icon name="MapPin" size={14} className="mt-0.5 flex-shrink-0" />
                        <span>{venue?.address}</span>
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
                    {venue?.participants?.map((participant, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-2 p-2 bg-background rounded-lg border border-border"
                      >
                        <Icon name="User" size={14} className="text-primary flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-foreground truncate">
                            {participant?.name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            <span className="font-semibold data-text">{participant?.travelTime}</span> via {participant?.method}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {venue?.features && venue?.features?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {venue?.features?.map((feature, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-muted text-muted-foreground rounded text-xs"
                        >
                          <Icon name="Check" size={10} />
                          {feature}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex lg:flex-col gap-2 lg:items-end">
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => onViewDetails(venue)}
                    iconName="ArrowRight"
                    iconPosition="right"
                    className="flex-1 lg:flex-none"
                  >
                    View Details
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="ExternalLink"
                    iconPosition="right"
                    className="flex-1 lg:flex-none"
                  >
                    Map
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FilteredResultsList;